import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { OverlayTrigger, Popover, CloseButton } from "react-bootstrap";

import { getStockCompanyIcon, debounce } from "utils";

import { Avatar } from "components/Avatar";
import { useNavigation } from "utils/navigate";
import { InputSearch } from "components/Form";
import defaultStrategyImage from "assets/icons/Button utility.png";
import { useMarketplaceStrategies } from "features/Explore/hook/useMarketplaceStrategies";
import { useExploreStocks } from "features/Market/hook";
import { useInfiniteScrollSentinel } from "hooks/useInfiniteScrollSentinel";
import { SearchContainer, SearchPopover } from "../Navbar.styles";
import TabsSwitcher from "features/Strategy/v2/ETFBuilder/Components/CompanyDetails/TabSwitcher";
import { SearchListSkeleton } from "./SearchListSkeleton";

export const SEARCH_TAB_KEYS = {
  STRATEGIES: "strategies",
  STOCK: "stock",
};

export const SEARCH_TABS = [
  { key: SEARCH_TAB_KEYS.STRATEGIES, label: "Strategies" },
  { key: SEARCH_TAB_KEYS.STOCK, label: "Stocks" },
];

export const SKELETON_ROWS = 6;

const PAGE_SIZE = 20;

export const Search = (props) => {
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const [inputValue, setInputValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeTab, setActiveTab] = useState(SEARCH_TAB_KEYS.STRATEGIES);
  const [show, setShow] = useState(false);

  const {
    strategies,
    isLoading: isLoadingStrategies,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMarketplaceStrategies({
    search: activeTab === SEARCH_TAB_KEYS.STRATEGIES ? debouncedQuery : "",
    category: "equity",
    size: PAGE_SIZE,
    enabled: show && activeTab === SEARCH_TAB_KEYS.STRATEGIES,
  });

  const {
    stocksData,
    isLoading: isLoadingStocks,
    fetchNextPage: fetchNextStocksPage,
    hasNextPage: hasNextStocksPage,
    isFetchingNextPage: isFetchingNextStocksPage,
  } = useExploreStocks({
    search: activeTab === SEARCH_TAB_KEYS.STOCK ? debouncedQuery : "",
    size: PAGE_SIZE,
    enabled: show && activeTab === SEARCH_TAB_KEYS.STOCK,
  });

  const { navigateToV2StockDetails, navigateToV2MarketplaceDetail } =
    useNavigation();

  const { listRef: strategyListRef, sentinelRef: strategySentinelRef } =
    useInfiniteScrollSentinel({
      enabled: show && activeTab === SEARCH_TAB_KEYS.STRATEGIES,
      hasNextPage,
      isFetchingNextPage,
      isLoading: isLoadingStrategies,
      onLoadMore: fetchNextPage,
    });

  const { listRef: stockListRef, sentinelRef: stockSentinelRef } =
    useInfiniteScrollSentinel({
      enabled: show && activeTab === SEARCH_TAB_KEYS.STOCK,
      hasNextPage: hasNextStocksPage,
      isFetchingNextPage: isFetchingNextStocksPage,
      isLoading: isLoadingStocks,
      onLoadMore: fetchNextStocksPage,
    });

  const handleTabClick = (filter) => {
    inputRef.current?.focus();
    setActiveTab(filter.key);
    setDebouncedQuery(inputValue.trim());
    setShow(true);
  };

  const resetSearch = () => {
    setInputValue("");
    setDebouncedQuery("");
  };

  const handleStockClick = (symbol) => {
    navigateToV2StockDetails(symbol);
    resetSearch();
    setShow(false);
    document.body.click();
  };

  const handleStategyClick = (id) => {
    navigateToV2MarketplaceDetail(id);
    resetSearch();
    setShow(false);
    document.body.click();
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedQuery(value.trim());
      }, 600),
    []
  );

  useEffect(() => {
    if (!inputValue) {
      setDebouncedQuery("");
    }
  }, [inputValue]);

  const handleSearch = (e) => {
    const { value } = e.target;
    setInputValue(value);
    setShow(true);
    debouncedSearch(value);
  };

  const clearSearch = () => {
    resetSearch();
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    document.body.click();
  };

  const handleToggle = (value) => {
    if (!value) {
      setShow(false);
      return;
    }
    setShow(true);
  };

  return (
    <SearchContainer>
      <OverlayTrigger
        rootClose
        trigger={["click"]}
        placement="bottom-start"
        arrowOffsetLeft={100}
        show={show}
        onToggle={handleToggle}
        overlay={
          <SearchPopover>
            <Popover.Body ref={wrapperRef}>
              <div className="tab-category-wrapper">
                <TabsSwitcher
                  tabs={SEARCH_TABS}
                  activeTab={activeTab}
                  onChange={(key) => handleTabClick({ key })}
                  variant="underline"
                />

                <div className="mobile-view pt-0">
                  <CloseButton onClick={handleClose} />
                </div>
              </div>

              {activeTab === SEARCH_TAB_KEYS.STOCK && (
                <div className="search-list" ref={stockListRef}>
                  {!isLoadingStocks &&
                    stocksData.map((stock, inx) => (
                      <div
                        className="search-item"
                        onClick={() => handleStockClick(stock.symbol)}
                        key={`${stock.symbol}-${inx}`}
                      >
                        <div className="img-box">
                          <Avatar
                            src={getStockCompanyIcon(stock.symbol)}
                            name={stock.symbol}
                            size="small"
                          />
                        </div>
                        <div
                          className="symbol-text"
                          title={`${stock.symbol} - ${stock.companyName || ""}`}
                        >
                          <div className="symbol-title">{stock.symbol}</div>
                          <div className="symbol-desc">{stock.companyName}</div>
                        </div>
                      </div>
                    ))}

                  {isLoadingStocks && !stocksData.length && (
                    <SearchListSkeleton
                      count={SKELETON_ROWS}
                      type="stock-initial"
                    />
                  )}

                  {!isLoadingStocks && !stocksData.length && (
                    <div className="no-results">
                      <div className="no-results-text">No stocks found</div>
                      <div className="no-results-desc">
                        Try searching for a different stock symbol or name
                      </div>
                    </div>
                  )}

                  {isFetchingNextStocksPage && (
                    <SearchListSkeleton
                      count={SKELETON_ROWS / 2}
                      type="stock-next"
                    />
                  )}

                  <div ref={stockSentinelRef} className="scroll-sentinel" />
                </div>
              )}

              {activeTab === SEARCH_TAB_KEYS.STRATEGIES && (
                <div className="search-list" ref={strategyListRef}>
                  {!isLoadingStrategies &&
                    strategies.map((strategy, inx) => (
                      <div
                        className="search-item strategy-search-item"
                        onClick={() => handleStategyClick(strategy.strategy_id)}
                        key={inx}
                      >
                        <img
                          className="strategy-thumb"
                          src={strategy.strategy_image || defaultStrategyImage}
                          alt={strategy.name}
                        />
                        <div className="strategy-text">{strategy.name}</div>
                      </div>
                    ))}

                  {isLoadingStrategies && !strategies.length && (
                    <SearchListSkeleton
                      count={SKELETON_ROWS}
                      type="strategy-initial"
                    />
                  )}

                  {!isLoadingStrategies && !strategies.length && (
                    <div className="no-results">
                      <div className="no-results-text">No strategies found</div>
                      <div className="no-results-desc">
                        Try searching for a different strategy name
                      </div>
                    </div>
                  )}

                  {isFetchingNextPage && (
                    <SearchListSkeleton
                      count={SKELETON_ROWS / 2}
                      type="strategy-next"
                    />
                  )}

                  <div ref={strategySentinelRef} className="scroll-sentinel" />
                </div>
              )}
            </Popover.Body>
          </SearchPopover>
        }
      >
        <div>
          <InputSearch
            id={`${props.id}-search`}
            placeholder="Find an investment"
            ref={inputRef}
            value={inputValue}
            handleChange={handleSearch}
            clearSearch={clearSearch}
          />
        </div>
      </OverlayTrigger>
    </SearchContainer>
  );
};

Search.propTypes = {
  id: PropTypes.string,
};
